import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import IncidentFirstSteps from './IncidentFirstSteps'
import {
  DETECTION_SOURCES,
  INCIDENT_SEVERITIES,
} from '../../services/incidentService'
import {
  INCIDENT_CATEGORIES,
  REPORT_WIZARD_STEPS,
  SEVERITY_HINTS,
  buildIncidentPayload,
  emptyIncidentDraft,
  getIncidentCategory,
  validateIncidentDraft,
} from '../../utils/incidentReport'
import { severityColor } from '../../utils/incidentTable'
import '../../styles/ticket-form.css'

/** Arma el contexto que recibe la IA a partir del borrador. */
const reportContext = (draft, assets) => ({
  category: draft.category,
  description: draft.description.trim(),
  severity: draft.severity,
  detectionSource: draft.detectionSource,
  personalData: draft.personalData,
  assets: assets
    .filter((a) => draft.assetIds.includes(a.Id))
    .map((a) => a.Name),
})

/**
 * Reporte de incidente "paso a paso": una pregunta por pantalla y, antes de
 * enviar, los primeros pasos a seguir generados con IA. Los pasos y qué
 * valida cada uno viven en `REPORT_WIZARD_STEPS`.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.assets - Catálogo de activos del SGSI.
 * @param {string} props.author - Nombre de quien reporta.
 * @param {boolean} props.saving
 * @param {Object} props.firstSteps - Estado de `useIncidentFirstSteps`.
 * @param {(payload: Object, assetIds: number[]) => Promise<boolean>} props.onSubmit
 * @returns {JSX.Element}
 */
const IncidentReportWizard = ({
  assets,
  author,
  saving,
  firstSteps,
  onSubmit,
}) => {
  const [draft, setDraft] = useState(() => emptyIncidentDraft(author))
  const [errors, setErrors] = useState({})
  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const [assetQuery, setAssetQuery] = useState('')

  const step = REPORT_WIZARD_STEPS[index]
  const isLast = index === REPORT_WIZARD_STEPS.length - 1
  const { generate } = firstSteps

  const set = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  // Al llegar al paso de primeros pasos, pedir la guía (sólo si cambió algo).
  useEffect(() => {
    if (step.key === 'firstSteps') generate(reportContext(draft, assets))
    // Sólo al entrar al paso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.key])

  const filteredAssets = useMemo(() => {
    const term = assetQuery.trim().toLowerCase()
    return term
      ? assets.filter((a) =>
          `${a.Name} ${a.Code} ${a.Type}`.toLowerCase().includes(term)
        )
      : assets
  }, [assets, assetQuery])

  const goTo = (target) => {
    setDirection(target > index ? 1 : -1)
    setIndex(target)
  }

  const next = async () => {
    const found = validateIncidentDraft(draft, step.fields)
    setErrors(found)
    if (Object.keys(found).length) return
    if (isLast) await onSubmit(buildIncidentPayload(draft), draft.assetIds)
    else goTo(index + 1)
  }

  const category = getIncidentCategory(draft.category)

  /** Contenido de cada paso, por clave de `REPORT_WIZARD_STEPS`. */
  const STEP_CONTENT = {
    what: () => (
      <div className="d-flex flex-column gap-3">
        <div className="irw-grid">
          {INCIDENT_CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`at-origin-card ${draft.category === c.key ? 'is-active' : ''}`}
              onClick={() => set('category', c.key)}
              aria-pressed={draft.category === c.key}
            >
              <i className={`fa-solid ${c.icon}`} />
              <span>{c.label}</span>
            </button>
          ))}
        </div>
        <div>
          <label className="at-label" htmlFor="irw-desc">
            Cuéntenos qué pasó <span className="at-req">*</span>
          </label>
          <textarea
            id="irw-desc"
            className={`form-control at-input ${errors.description ? 'is-invalid' : ''}`}
            rows={4}
            placeholder="Ej: Recibí un correo del «banco» pidiendo mi clave y le di clic al enlace…"
            value={draft.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>
      </div>
    ),
    when: () => (
      <div className="d-flex flex-column gap-3">
        <div style={{ maxWidth: 280 }}>
          <label className="at-label" htmlFor="irw-when">
            Fecha y hora aproximada <span className="at-req">*</span>
          </label>
          <input
            id="irw-when"
            type="datetime-local"
            className="form-control at-input"
            value={draft.detectedAt}
            onChange={(e) => set('detectedAt', e.target.value)}
          />
        </div>
        <div>
          <label className="at-label">¿Cómo se dio cuenta?</label>
          <div className="irw-chips">
            {DETECTION_SOURCES.map((source) => (
              <button
                key={source}
                type="button"
                className={`irw-chip ${draft.detectionSource === source ? 'is-active' : ''}`}
                onClick={() =>
                  set(
                    'detectionSource',
                    draft.detectionSource === source ? '' : source
                  )
                }
                aria-pressed={draft.detectionSource === source}
              >
                {source}
              </button>
            ))}
          </div>
        </div>
      </div>
    ),
    impact: () => (
      <div className="d-flex flex-column gap-3">
        <div className="irw-sev">
          {INCIDENT_SEVERITIES.map((level) => {
            const color = severityColor(level)
            const active = draft.severity === level
            return (
              <button
                key={level}
                type="button"
                className={`irw-sev-card ${active ? 'is-active' : ''}`}
                style={
                  active
                    ? { borderColor: color, background: `${color}12` }
                    : undefined
                }
                onClick={() => set('severity', level)}
                aria-pressed={active}
              >
                <span className="irw-sev-name" style={{ color }}>
                  {level}
                </span>
                <span className="irw-sev-hint">{SEVERITY_HINTS[level]}</span>
              </button>
            )
          })}
        </div>
        <label className="irw-check">
          <input
            type="checkbox"
            className="form-check-input m-0"
            checked={draft.personalData}
            onChange={(e) => set('personalData', e.target.checked)}
          />
          <span>
            Pudo exponerse información de personas (clientes, colaboradores:
            nombres, cédulas, datos de contacto…)
          </span>
        </label>
      </div>
    ),
    assets: () => (
      <div className="d-flex flex-column gap-2">
        <input
          type="search"
          className="form-control form-control-sm at-input"
          placeholder="Buscar equipo o sistema…"
          value={assetQuery}
          onChange={(e) => setAssetQuery(e.target.value)}
        />
        <div className="irw-assets">
          {filteredAssets.length === 0 ? (
            <p className="at-level-hint p-2 mb-0">
              No hay activos que coincidan.
            </p>
          ) : (
            filteredAssets.map((asset) => (
              <label key={asset.Id} className="irw-asset">
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={draft.assetIds.includes(asset.Id)}
                  onChange={() =>
                    set(
                      'assetIds',
                      draft.assetIds.includes(asset.Id)
                        ? draft.assetIds.filter((id) => id !== asset.Id)
                        : [...draft.assetIds, asset.Id]
                    )
                  }
                />
                <span className="flex-grow-1 min-w-0">
                  <span className="d-block fw-medium text-truncate">
                    {asset.Name}
                  </span>
                  <span className="irw-asset-meta">
                    {asset.Code} · {asset.Type}
                  </span>
                </span>
              </label>
            ))
          )}
        </div>
        <span className="at-level-hint">
          {draft.assetIds.length} seleccionado(s)
        </span>
      </div>
    ),
    firstSteps: () => (
      <IncidentFirstSteps
        guide={firstSteps.guide}
        loading={firstSteps.loading}
        aiError={firstSteps.aiError}
        onRetry={firstSteps.retry}
      />
    ),
    review: () => (
      <dl className="irw-review">
        <dt>Tipo</dt>
        <dd>
          {category && <i className={`fa-solid ${category.icon} me-2`} />}
          {category?.label}
        </dd>
        <dt>Descripción</dt>
        <dd className="irw-pre">{draft.description}</dd>
        <dt>Detectado</dt>
        <dd>
          {new Date(draft.detectedAt).toLocaleString('es-CR', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
          {draft.detectionSource && ` · ${draft.detectionSource}`}
        </dd>
        <dt>Severidad</dt>
        <dd style={{ color: severityColor(draft.severity), fontWeight: 600 }}>
          {draft.severity}
        </dd>
        <dt>Datos personales</dt>
        <dd>{draft.personalData ? 'Sí, posiblemente' : 'No'}</dd>
        <dt>Activos</dt>
        <dd>
          {assets
            .filter((a) => draft.assetIds.includes(a.Id))
            .map((a) => a.Name)
            .join(', ') || 'No indicados'}
        </dd>
        <dt>Reporta</dt>
        <dd>{draft.reportedBy || '—'}</dd>
      </dl>
    ),
  }

  const stepErrors = step.fields.map((f) => errors[f]).filter(Boolean)

  return (
    <div className="wz">
      <div className="wz-progress" aria-label="Progreso">
        {REPORT_WIZARD_STEPS.map((s, i) => (
          <button
            key={s.key}
            type="button"
            className={`wz-dot ${i === index ? 'is-current' : ''} ${i < index ? 'is-done' : ''}`}
            onClick={() => i < index && goTo(i)}
            disabled={i >= index}
            title={s.title}
            aria-current={i === index ? 'step' : undefined}
          >
            <i className={`fa-solid ${i < index ? 'fa-check' : s.icon}`} />
          </button>
        ))}
        <div className="wz-bar">
          <span
            style={{
              width: `${(index / (REPORT_WIZARD_STEPS.length - 1)) * 100}%`,
            }}
          />
        </div>
      </div>

      <div className="at-card wz-card">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step.key}
            initial={{ opacity: 0, x: 24 * direction }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 * direction }}
            transition={{ duration: 0.18 }}
          >
            <span className="wz-count">
              Paso {index + 1} de {REPORT_WIZARD_STEPS.length}
            </span>
            <h5 className="wz-title">{step.title}</h5>
            <p className="wz-subtitle">{step.subtitle}</p>

            {STEP_CONTENT[step.key]()}

            {stepErrors.map((msg) => (
              <p key={msg} className="at-error-text">
                <i className="fa-solid fa-circle-exclamation me-1" />
                {msg}
              </p>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="at-actions justify-content-between">
        <button
          type="button"
          className="btn at-btn-ghost"
          onClick={() => goTo(index - 1)}
          disabled={index === 0 || saving}
        >
          <i className="fa-solid fa-arrow-left me-2" />
          Atrás
        </button>
        <button
          type="button"
          className="btn at-btn-primary"
          onClick={next}
          disabled={saving || (step.key === 'firstSteps' && firstSteps.loading)}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Enviando…
            </>
          ) : isLast ? (
            <>
              <i className="fa-solid fa-paper-plane me-2" />
              Enviar reporte
            </>
          ) : (
            <>
              {step.key === 'assets' && draft.assetIds.length === 0
                ? 'Omitir'
                : step.key === 'firstSteps'
                  ? 'Entendido, continuar'
                  : 'Siguiente'}
              <i className="fa-solid fa-arrow-right ms-2" />
            </>
          )}
        </button>
      </div>

      <style>{`
        .wz { display: flex; flex-direction: column; gap: 16px; }
        .wz-progress { position: relative; display: flex; justify-content: space-between; padding: 0 4px; }
        .wz-bar { position: absolute; left: 20px; right: 20px; top: 50%; height: 3px; background: var(--gm-border); z-index: 0; border-radius: 99px; }
        .wz-bar span { display: block; height: 100%; background: var(--gm-primary); border-radius: 99px; transition: width 0.3s ease; }
        .wz-dot {
          position: relative; z-index: 1; width: 34px; height: 34px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center; font-size: 0.75rem;
          border: 2px solid var(--gm-border); background: var(--gm-surface); color: var(--gm-text-faint);
        }
        .wz-dot.is-done { border-color: var(--gm-primary); background: var(--gm-primary); color: #fff; cursor: pointer; }
        .wz-dot.is-current { border-color: var(--gm-primary); color: var(--gm-primary); box-shadow: 0 0 0 4px var(--gm-primary-soft); }
        .wz-card { min-height: 320px; padding: 24px; overflow: hidden; }
        .wz-count { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--gm-primary); }
        .wz-title { font-weight: 700; margin: 4px 0 2px; color: var(--gm-text); }
        .wz-subtitle { font-size: 0.88rem; color: var(--gm-text-muted); margin-bottom: 18px; }

        .irw-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 8px; }
        .irw-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .irw-chip {
          height: 30px; padding: 0 12px; border-radius: 99px; border: 1px solid var(--gm-border);
          background: transparent; color: var(--gm-text-muted); font-size: 0.8rem;
        }
        .irw-chip:hover { color: var(--gm-text); border-color: var(--gm-text-faint); }
        .irw-chip.is-active { border-color: var(--gm-primary); background: var(--gm-primary-soft); color: var(--gm-primary); font-weight: 600; }
        .irw-sev { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 8px; }
        .irw-sev-card {
          display: flex; flex-direction: column; gap: 4px; padding: 12px; text-align: left;
          border: 1px solid var(--gm-border); border-radius: 12px; background: transparent;
        }
        .irw-sev-name { font-weight: 700; font-size: 0.92rem; }
        .irw-sev-hint { font-size: 0.76rem; color: var(--gm-text-muted); }
        .irw-check { display: flex; gap: 10px; align-items: flex-start; font-size: 0.85rem; color: var(--gm-text); cursor: pointer; }
        .irw-check input { margin-top: 3px !important; }
        .irw-assets { max-height: 260px; overflow-y: auto; border: 1px solid var(--gm-border); border-radius: 10px; padding: 4px; }
        .irw-asset { display: flex; gap: 10px; align-items: center; padding: 6px 8px; border-radius: 8px; font-size: 0.84rem; cursor: pointer; }
        .irw-asset:hover { background: var(--gm-surface-2); }
        .irw-asset-meta { display: block; font-size: 0.72rem; color: var(--gm-text-muted); }
        .irw-review { display: grid; grid-template-columns: 140px 1fr; gap: 8px 16px; margin: 0; font-size: 0.86rem; }
        .irw-review dt { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--gm-text-muted); padding-top: 2px; }
        .irw-review dd { margin: 0; color: var(--gm-text); }
        .irw-pre { white-space: pre-wrap; word-break: break-word; }
        @media (max-width: 575px) { .irw-review { grid-template-columns: 1fr; gap: 2px; } .irw-review dd { margin-bottom: 8px; } }
      `}</style>
    </div>
  )
}

export default IncidentReportWizard
